import React from 'react';
import Box from '@mui/system/Box';
import { useStacklineTheme } from '@stackline/ui';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  IntegrationTab,
  IntegrationsNavigationTabs
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Integrations/SettingsIntegrationsPageLayout';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import colors from 'src/utils/colors';
import { useSalsifyIntegration } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Integrations/useSalsifyIntegration';
import { SalsifyIcon } from 'src/components/SvgIcons';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';

interface SettingsIntegrationsSyndicationProps {
  tab: IntegrationTab;
  setTab: (tab: IntegrationTab) => void;
}

interface SyndicationPlatform {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  isAvailable: boolean;
}

interface IntegrationCardProps {
  platform: SyndicationPlatform;
  isConnected?: boolean;
  needsReconnect?: boolean;
  onConnect?: () => void;
}

type IntegrationStatus = 'coming_soon' | 'connected' | 'reconnect' | 'connect';

const BUTTON_TEXT_MAP: Record<IntegrationStatus, string> = {
  coming_soon: 'Coming soon',
  connected: 'Connected',
  reconnect: 'Reconnect',
  connect: 'Connect'
};

/**
 * Skeleton card for loading state
 */
const skeletonSx = { backgroundColor: colors.graysPrimary };

const IntegrationCardSkeleton = () => {
  return (
    <Box
      sx={{
        border: `1px solid ${colors.graysSecondary}`,
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '16px'
      }}
    >
      <Box>
        <Flex alignItems="center" gap={8} marginBottom="8px">
          <SlSkeleton variant="circular" width={40} height={40} sx={skeletonSx} />
          <SlSkeleton variant="text" width={100} height={24} sx={skeletonSx} />
        </Flex>
        <SlSkeleton variant="text" width="100%" height={16} sx={skeletonSx} />
        <SlSkeleton variant="text" width="80%" height={16} sx={skeletonSx} />
      </Box>
      <Flex justifyContent="flex-end">
        <SlSkeleton variant="rounded" width={80} height={28} sx={{ borderRadius: '16px', ...skeletonSx }} />
      </Flex>
    </Box>
  );
};

/**
 * Divider component for separating sections
 */
const SectionDivider = () => (
  <Box
    sx={{
      height: '1px',
      backgroundColor: colors.dividerGrey,
      marginTop: '32px',
      marginBottom: '32px'
    }}
  />
);

/**
 * Card component for each syndication integration
 */
const IntegrationCard = ({
  platform,
  isConnected = false,
  needsReconnect = false,
  onConnect
}: IntegrationCardProps) => {
  const getStatus = (): IntegrationStatus => {
    if (!platform.isAvailable) return 'coming_soon';
    if (isConnected) return 'connected';
    if (needsReconnect) return 'reconnect';
    return 'connect';
  };

  const status = getStatus();
  const buttonText = BUTTON_TEXT_MAP[status];
  const isDisabled = status === 'coming_soon' || status === 'connected';

  const getButtonContent = () => {
    if (status === 'coming_soon') {
      return (
        <Text variant="body2" sx={{ color: colors.lightGrey, fontWeight: 500 }}>
          {buttonText}
        </Text>
      );
    }

    return (
      <SlButton
        variant="normal"
        onClick={onConnect}
        disabled={isDisabled}
        buttonSx={{
          borderRadius: '16px',
          padding: '3px 20px'
        }}
      >
        {buttonText}
      </SlButton>
    );
  };

  return (
    <Box
      sx={{
        border: `1px solid ${colors.graysSecondary}`,
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '16px'
      }}
    >
      <Box>
        <Flex alignItems="center" gap={8} marginBottom="8px">
          {platform.icon}
          <Text variant="h6">{platform.name}</Text>
        </Flex>
        <Text variant="body2" sx={{ fontSize: '14px' }}>
          {platform.description}
        </Text>
      </Box>

      <Flex justifyContent="flex-end">
        {getButtonContent()}
      </Flex>
    </Box>
  );
};

/**
 * Grid container for integration cards
 */
const IntegrationGrid = ({ children }: { children: React.ReactNode }) => {
  const theme = useStacklineTheme();
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: theme.spacing.lg,
        maxWidth: '1100px'
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Syndication integrations page - displays available PIM/syndication platform connections
 */
const SettingsIntegrationsSyndication = ({ tab, setTab }: SettingsIntegrationsSyndicationProps) => {
  const theme = useStacklineTheme();

  // Salsify integration hook
  const salsify = useSalsifyIntegration();

  // Define all syndication platforms
  const allPlatforms: SyndicationPlatform[] = [
    {
      id: 'salsify',
      name: 'Salsify',
      description: 'Cloud-based tools to manage and optimize digital product experiences.',
      icon: <SalsifyIcon style={{ width: 40, height: 40 }} />,
      isAvailable: true
    }
    // Future platforms can be added here
  ];

  // Split platforms into connected and available
  // isConnected is now already the "fully connected with valid token" state
  const connectedPlatforms = allPlatforms.filter((platform) => {
    if (platform.id === 'salsify') return salsify.isConnected;
    return false; // Add logic for other platforms here
  });

  const availablePlatforms = allPlatforms.filter((platform) => {
    if (platform.id === 'salsify') return !salsify.isConnected;
    return true; // Show other platforms as available by default
  });

  const hasConnected = connectedPlatforms.length > 0;
  const hasAvailable = availablePlatforms.length > 0;
  const showBothSections = hasConnected && hasAvailable;

  // Show loading state with skeletons
  if (salsify.isLoading) {
    return (
      <BasePageContainer sx={{ marginTop: '0px' }}>
        <Box maxWidth="1200px">
          <Text variant="h2">Integrations</Text>
          <Box marginBottom={theme.spacing.lg}>
            <IntegrationsNavigationTabs tab={tab} setTab={setTab} />
          </Box>

          {/* Connected Section Skeleton */}
          <Flex flexDirection="column" marginBottom={theme.spacing.md}>
            <SlSkeleton variant="text" width={120} height={28} sx={skeletonSx} />
          </Flex>
          <IntegrationGrid>
            <IntegrationCardSkeleton />
          </IntegrationGrid>

          <SectionDivider />

          {/* Available Section Skeleton */}
          <Flex flexDirection="column" marginBottom={theme.spacing.md}>
            <SlSkeleton variant="text" width={100} height={28} sx={skeletonSx} />
          </Flex>
          <IntegrationGrid>
            <IntegrationCardSkeleton />
          </IntegrationGrid>
        </Box>
      </BasePageContainer>
    );
  }

  return (
    <BasePageContainer sx={{ marginTop: '0px' }}>
      <Box maxWidth="1200px">
        <Text variant="h2">Integrations</Text>
        <Box marginBottom={theme.spacing.lg}>
          <IntegrationsNavigationTabs tab={tab} setTab={setTab} />
        </Box>

        {/* Connected Section - only show if there are connected platforms */}
        {hasConnected && (
          <>
            <Flex flexDirection="column" marginBottom={theme.spacing.md}>
              <Text variant="h4">Connected</Text>
            </Flex>
            <IntegrationGrid>
              {connectedPlatforms.map((platform) => (
                <IntegrationCard
                  key={platform.id}
                  platform={platform}
                  isConnected={true}
                  onConnect={platform.id === 'salsify' ? salsify.handleConnect : undefined}
                />
              ))}
            </IntegrationGrid>
          </>
        )}

        {/* Divider - only show if both sections have content */}
        {showBothSections && <SectionDivider />}

        {/* Available Section - only show if there are available platforms */}
        {hasAvailable && (
          <>
            <Flex flexDirection="column" marginBottom={theme.spacing.md}>
              <Text variant="h4">Available</Text>
            </Flex>
            <IntegrationGrid>
              {availablePlatforms.map((platform) => (
                <IntegrationCard
                  key={platform.id}
                  platform={platform}
                  isConnected={false}
                  needsReconnect={platform.id === 'salsify' ? salsify.needsReconnect : false}
                  onConnect={platform.id === 'salsify' ? salsify.handleConnect : undefined}
                />
              ))}
            </IntegrationGrid>
          </>
        )}
      </Box>
    </BasePageContainer>
  );
};

export default SettingsIntegrationsSyndication;
